/**
 * UI Slice
 * Manages UI state (loading, modals, sidebar, theme, etc.)
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

// ===========================================
// Types
// ===========================================

export interface ModalState {
    isOpen: boolean;
    type: string | null;
    data?: unknown;
}

export interface UIState {
    isLoading: boolean;
    isSidebarOpen: boolean;
    isMobileMenuOpen: boolean;
    theme: 'light' | 'dark';
    modal: ModalState;
    notifications: Notification[];
}

export interface Notification {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    duration?: number;
}

// ===========================================
// Initial State
// ===========================================

const initialState: UIState = {
    isLoading: false,
    isSidebarOpen: true,
    isMobileMenuOpen: false,
    theme: 'light',
    modal: {
        isOpen: false,
        type: null,
        data: undefined,
    },
    notifications: [],
};

// ===========================================
// Slice
// ===========================================

export const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        // Loading
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        // Sidebar
        toggleSidebar: (state) => {
            state.isSidebarOpen = !state.isSidebarOpen;
        },
        setSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.isSidebarOpen = action.payload;
        },

        // Mobile Menu
        toggleMobileMenu: (state) => {
            state.isMobileMenuOpen = !state.isMobileMenuOpen;
        },
        setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
            state.isMobileMenuOpen = action.payload;
        },

        // Theme
        setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
            state.theme = action.payload;
        },
        toggleTheme: (state) => {
            state.theme = state.theme === 'light' ? 'dark' : 'light';
        },

        // Modal
        openModal: (state, action: PayloadAction<{ type: string; data?: unknown }>) => {
            state.modal = {
                isOpen: true,
                type: action.payload.type,
                data: action.payload.data,
            };
        },
        closeModal: (state) => {
            state.modal = {
                isOpen: false,
                type: null,
                data: undefined,
            };
        },

        // Notifications
        addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
            const id = Date.now().toString();
            state.notifications.push({ ...action.payload, id });
        },
        removeNotification: (state, action: PayloadAction<string>) => {
            state.notifications = state.notifications.filter((n) => n.id !== action.payload);
        },
        clearNotifications: (state) => {
            state.notifications = [];
        },
    },
});

// Actions
export const {
    setLoading,
    toggleSidebar,
    setSidebarOpen,
    toggleMobileMenu,
    setMobileMenuOpen,
    setTheme,
    toggleTheme,
    openModal,
    closeModal,
    addNotification,
    removeNotification,
    clearNotifications,
} = uiSlice.actions;

// Selectors
export const selectIsLoading = (state: RootState) => state.ui.isLoading;
export const selectIsSidebarOpen = (state: RootState) => state.ui.isSidebarOpen;
export const selectIsMobileMenuOpen = (state: RootState) => state.ui.isMobileMenuOpen;
export const selectTheme = (state: RootState) => state.ui.theme;
export const selectModal = (state: RootState) => state.ui.modal;
export const selectNotifications = (state: RootState) => state.ui.notifications;

export default uiSlice.reducer;
